import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import ImageUploadField from "../../components/form/ImageUploadField";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { VehicleFormData } from "../../types/vehicle";
import { vehicleImageUrl } from "../../utils/vehicleImage";

interface DriverOption {
  id: string;
  fullName: string;
}

interface VehicleTypeOption {
  id: string;
  name?: string | null;
  description: string;
}

const initialFormData: VehicleFormData = {
  driverId: "",
  vehicleTypeId: "",
  registration: "",
  make: "",
  model: "",
  color: "",
  imageName: null,
  status: true,
};

async function saveVehicle(
  endpoint: string,
  method: "post" | "put",
  payload: Record<string, unknown>,
  imageFile: File | null,
) {
  if (imageFile) {
    const formData = new FormData();
    formData.append("data", JSON.stringify(payload));
    formData.append("image", imageFile);
    return axiosInstance.request({
      method,
      url: endpoint,
      data: formData,
      transformRequest: (data, headers) => {
        if (data instanceof FormData) {
          delete (headers as Record<string, unknown>)["Content-Type"];
        }
        return data;
      },
    });
  }

  return method === "post"
    ? axiosInstance.post(endpoint, payload)
    : axiosInstance.put(endpoint, payload);
}

export default function VehicleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<VehicleFormData>(initialFormData);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchLookups();
    if (isEditMode) {
      fetchVehicleData();
    }
  }, [id]);

  const fetchLookups = async () => {
    try {
      const [driversRes, typesRes] = await Promise.all([
        axiosInstance.get<DriverOption[]>("/drivers"),
        axiosInstance.get<VehicleTypeOption[]>("/vehicletypes"),
      ]);
      setDrivers(driversRes.data);
      setVehicleTypes(typesRes.data);
    } catch (lookupError) {
      console.error("Error fetching vehicle lookups:", lookupError);
    }
  };

  const fetchVehicleData = async () => {
    try {
      const response = await axiosInstance.get(`/vehicles/${id}`);
      const data = response.data;
      setFormData({
        driverId: data.driverId ?? "",
        vehicleTypeId: data.vehicleTypeId ?? "",
        registration: data.registration ?? "",
        make: data.make ?? "",
        model: data.model ?? "",
        color: data.color ?? "",
        imageName: data.imageName ?? null,
        status: Boolean(data.status),
      });
    } catch (fetchError) {
      setError("Failed to fetch vehicle data");
      console.error("Error fetching vehicle:", fetchError);
    }
  };

  const buildPayload = () => ({
    driverId: formData.driverId || null,
    vehicleTypeId: formData.vehicleTypeId || null,
    registration: formData.registration.trim(),
    make: formData.make.trim(),
    model: formData.model.trim(),
    color: formData.color.trim() || null,
    imageName: formData.imageName,
    status: formData.status,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = buildPayload();
      if (isEditMode) {
        await saveVehicle(`/vehicles/${id}`, "put", payload, imageFile);
      } else {
        await saveVehicle("/vehicles", "post", payload, imageFile);
      }
      navigate("/vehicles");
    } catch (submitError: unknown) {
      const axiosError = submitError as {
        response?: { data?: { message?: string | string[] } };
      };
      const message = axiosError.response?.data?.message;
      setError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Failed to save vehicle",
      );
      console.error("Error saving vehicle:", submitError);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "status" ? value === "true" : value,
    }));
  };

  return (
    <div className="w-full space-y-6">
      <PageBreadcrumb pageTitle={isEditMode ? "Edit Vehicle" : "Add Vehicle"} />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && (
          <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div>
            <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-white/90">
              Vehicle Details
            </h3>
            <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Vehicle Type
                </label>
                <select
                  name="vehicleTypeId"
                  value={formData.vehicleTypeId}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select vehicle type</option>
                  {vehicleTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name?.trim() || type.description}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Driver
                </label>
                <select
                  name="driverId"
                  value={formData.driverId}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Unassigned</option>
                  {drivers.map((driver) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.fullName}
                    </option>
                  ))}
                </select>
              </div>

              <InputWithLabel
                label="Registration"
                name="registration"
                value={formData.registration}
                onChange={handleInputChange}
                required
                placeholder="e.g. AB12 CDE"
              />
              <InputWithLabel
                label="Make"
                name="make"
                value={formData.make}
                onChange={handleInputChange}
                required
                placeholder="Enter make"
              />
              <InputWithLabel
                label="Model"
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                required
                placeholder="Enter model"
              />
              <InputWithLabel
                label="Color"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                placeholder="Enter color"
              />

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  name="status"
                  value={String(formData.status)}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <ImageUploadField
                existingImageName={formData.imageName}
                existingImageSrc={vehicleImageUrl(formData.imageName)}
                onImageNameChange={(imageName) =>
                  setFormData((prev) => ({ ...prev, imageName }))
                }
                onFileChange={setImageFile}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/vehicles")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
